import useRequestJSON from './hooks/useRequestJSON';
import { BaseResponse, ErrorResponse, PostJSON } from './request';
import { useCallback } from 'react';
import { IOption } from './form';
import { TermBase } from '../contexts/terms/types';

export type OptionsCategory = 'resolve' | 'snooze' | 'archive';

export interface IAccount {
  id: number;
  pin: string;
  isSuspended: boolean;
  consentByDefault: boolean;
  uploadSizeLimit: number;
  uploadRowLimit: number;
  bulkLimit: number;
  stripeCustomerId?: string;
  type: string;
  isEnterprise: boolean;
  isSCEnabled: boolean;
  isLightbeam: boolean;
  isFTEUEnabled: boolean;
  isFTEUDefault: boolean;
  isChatEnabled: boolean;
  isPushMessageEnabled: boolean;
  isTimeTrackedEnabled: boolean;
  emr?: string;
  marker?: string;
  broker?: string;
  source?: string;
  showEndedProgramAlerts: boolean;
  showAccountWideRecentActions: boolean;
}

export interface AccountResponse extends BaseResponse {
  account: IAccount;
}

interface AccountValue {
  account: IAccount;
  setAccount: (account: IAccount) => void;
}

export const useGetAccount = (): AccountValue => {
  const url = '/user/data_account/';
  const request = useCallback(
    () =>
      PostJSON({
        url,
        params: {}
      }),
    []
  );
  const { data, setData } = useRequestJSON<AccountResponse, AccountResponse>({
    request
  });
  const setAccount = (account: IAccount) => {
    setData({ ...data, account });
  };
  return {
    account: data?.account || null,
    setAccount: setAccount
  };
};

export interface IAccountOptions {
  reason: string[];
  misc: IOption[];
}

export interface IChatOptions {
  isDefaultSecure: boolean;
}

export const DEFAULT_CHAT_OPTIONS: IChatOptions = {
  isDefaultSecure: false
};

export interface IPushMessageOptions {
  isBulkAdminOnly: boolean;
}

export interface IAppOptions {
  beta?: string[];
}

export interface IOptoutOptions {
  sendOptoutReminders: boolean;
  optoutFrequency: number;
}

export interface IProactiveOutreachOptions {
  enabled: boolean;
  inactivityDays: number;
  frequencyDays: number;
}

export const DEFAULT_PROACTIVE_OUTREACH_OPTIONS: IProactiveOutreachOptions = {
  enabled: false,
  inactivityDays: 14,
  frequencyDays: 7
};

export const DEFAULT_PUSH_MESSAGE_OPTIONS: IPushMessageOptions = {
  isBulkAdminOnly: false
};

export const DEFAULT_RESOLVE_OPTIONS: IAccountOptions = {
  reason: [
    'Made adjustment to care plan',
    'Made appointment',
    'Patient is OK',
    'Patient is hospitalized',
    'Other'
  ],
  misc: [
    {
      label: 'Averted ED Visit',
      value: 'ed_averted'
    },
    {
      label: 'Patient Misentry',
      value: 'misentry'
    }
  ]
};

export const DEFAULT_SNOOZE_OPTIONS: IAccountOptions = {
  reason: ['No answer/busy', 'Left message', 'Invalid number', 'Other'],
  misc: []
};

export const DEFAULT_ARCHIVE_REASONS: IOption[] = [
  {
    label: 'Changed Providers',
    value: 'changed_provider'
  },
  {
    label: 'Graduated From Program',
    value: 'graduation'
  },
  {
    label: 'No Longer Interested',
    value: 'interest'
  },
  {
    label: 'Extensive Disengagement',
    value: 'unengaged'
  },
  {
    label: 'Other',
    value: 'other'
  }
];

export interface IAccountBaseSettings {
  resolveOptions: IAccountOptions;
  snoozeOptions: IAccountOptions;
  archiveOptions: IOption[];
  chatOptions: IChatOptions;
  appOptions: IAppOptions;
  pmOptions: IPushMessageOptions;
  organization: string;
  askCareSatisfaction: boolean;
  askNps: boolean;
  hideConnect: {
    [key: string]: boolean;
  };
  optoutOptions?: IOptoutOptions;
  proactiveOutreachOptions?: IProactiveOutreachOptions;
}

export interface IAccountSettings extends IAccountBaseSettings {
  id: number;
  accountId: number;
  terms: TermBase;
  createdAt: Date;
  updatedAt: Date;
  organization: string;
  askCareSatisfaction: boolean;
  askNps: boolean;
  hideConnect: {
    [key: string]: boolean;
  };
}

export interface AccountSettingsResponse extends BaseResponse {
  settings: IAccountSettings;
}

interface AccountSettingsValue {
  settings: IAccountBaseSettings;
  isLoading: boolean;
  setSettings: (settings: IAccountSettings) => void;
}

export const useGetAccountSettings = (): AccountSettingsValue => {
  const url = '/user/data_account_settings/';
  const request = useCallback(
    () =>
      PostJSON({
        url,
        params: {}
      }),
    []
  );
  const { data, setData, isLoading } = useRequestJSON<
    AccountSettingsResponse,
    AccountSettingsResponse
  >({
    request
  });
  const setSettings = (settings: IAccountSettings) => {
    setData({ ...data, settings });
  };
  return {
    isLoading,
    settings: {
      resolveOptions: data?.settings?.resolveOptions || DEFAULT_RESOLVE_OPTIONS,
      snoozeOptions: data?.settings?.snoozeOptions || DEFAULT_SNOOZE_OPTIONS,
      archiveOptions: data?.settings?.archiveOptions || DEFAULT_ARCHIVE_REASONS,
      chatOptions: data?.settings?.chatOptions || DEFAULT_CHAT_OPTIONS,
      pmOptions: data?.settings?.pmOptions || DEFAULT_PUSH_MESSAGE_OPTIONS,
      appOptions: data?.settings?.appOptions || null,
      organization: data?.settings?.organization,
      askCareSatisfaction: data?.settings?.askCareSatisfaction,
      askNps: data?.settings?.askNps,
      hideConnect: data?.settings?.hideConnect,
      optoutOptions: data?.settings?.optoutOptions,
      proactiveOutreachOptions:
        data?.settings?.proactiveOutreachOptions ||
        DEFAULT_PROACTIVE_OUTREACH_OPTIONS
    },
    setSettings
  };
};

interface UpdateAccountSettingsParams {
  id: number;
  appOptions?: IAppOptions;
  optoutOptions?: IOptoutOptions;
  proactiveOutreachOptions?: IProactiveOutreachOptions;
}

interface UpdateAccountSettingsResponse extends BaseResponse {
  settings: IAccountBaseSettings;
}

export const updateAccountSettings = (
  params: UpdateAccountSettingsParams
): Promise<UpdateAccountSettingsResponse | ErrorResponse> => {
  return PostJSON<UpdateAccountSettingsResponse>({
    url: '/user/edit_account_settings/',
    params
  });
};
